import * as React from "react";
import { connect } from "react-redux";
import Alert from "react-s-alert";
import { bindActionCreators } from "redux";
import { SubPageLayout } from "../../../components/layouts/index";
import { IRegistrationFormState } from "../../../reducers/reactReduxForms/index";
import { FC, useEffect, useMemo } from "react";
import { ChangeOptions, Form, ModelAction, actions } from "react-redux-form";
import * as TrialItemActions from "../../../actions/trialItem";
import { NotificationAlert } from "../../../components/common/index";
import { TextFormInput } from "../../../components/form/index";
import { FontAwesomeIcons } from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import { renderRegistrationFormSaveBlock } from "../../../helpers/alert";
import {
    RegistrationComponent,
    renderFormControls,
} from "../../../helpers/formHelper";
import { RegistrationFormComponent } from "../../../interfaces/forms/IRegistrationComponent";
import { ITmdState } from "../../../reducers/index";
import { ITrialItemReduxFormState } from "../../../reducers/reactReduxForms/trialItem";

import "./TrialItemCreateVersion.scss";

const reduxFormName: string = "reduxForms.trialItem";

interface TrialItemCreateVersionProps {
    currentTrialItem: Dtos.TrialItem;
    trialId?: number;
    form?: Dtos.TrialItem & IRegistrationFormState;
    formState?: Dtos.FormState;
    formProperties?: Dtos.FormProperty[];
    reduxFormState?: ITrialItemReduxFormState;
    trialItem?: Dtos.TrialItem;
    lookups?: Dtos.Lookup[];
    loadingTrialItem?: boolean;
    savingTrialItem?: boolean;
    creatingTrialItem?: boolean;
    createTrialItemSuccess?: boolean;
    validationFailures?: Dtos.ResponseError[];
    onClose?: Function;
    clearTrialItem?: (propertiesToClear?: string[]) => void;
    saveTrialItem?: (
        formData: Dtos.TrialItem,
        collectionFormData: Dtos.Collection
    ) => void;
    createTrialItem?: (trialId: number) => void;
    loadForm?: (model: string, value: any) => ModelAction;
    changeForm?: (
        model: string,
        value: any,
        options?: ChangeOptions
    ) => ModelAction;
}

const TrialItemCreateVersion: FC<TrialItemCreateVersionProps> = ({
    currentTrialItem,
    trialId,
    form,
    formState,
    formProperties,
    trialItem,
    lookups,
    loadingTrialItem,
    savingTrialItem,
    creatingTrialItem,
    createTrialItemSuccess,
    validationFailures,
    onClose,
    clearTrialItem,
    saveTrialItem,
    createTrialItem,
    loadForm,
    changeForm,
}) => {
    // Create TrialItem
    useEffect(() => {
        if (!trialItem && trialId) {
            createTrialItem(trialId);
        }
    }, [trialItem, trialId, createTrialItem]);

    // Load Form
    useEffect(() => {
        if (!creatingTrialItem && createTrialItemSuccess) {
            loadReduxForm();
        }
    }, [creatingTrialItem, createTrialItemSuccess, loadForm]);

    // Cleanup effect to clear the trial item on component unmount
    useEffect(() => {
        return () => {
            clearTrialItem();
        };
    }, [clearTrialItem]);

    const handleOnSubmit = (
        data: Dtos.TrialItem,
        currentTrialItem: Dtos.TrialItem
    ) => {
        if (!data.version) {
            handleOnSubmitFailed(data);
            return;
        }

        if (data) {
            const changes: Dtos.TrialItem = {
                trialId: currentTrialItem.trialId,
                trialItemTemplateId: currentTrialItem.trialItemTemplateId,
                active: true,
            } as Dtos.TrialItem;
            saveTrialItem({ ...data, ...changes }, null);
        }
        onCancel();
    };

    const handleOnSubmitFailed = (_data: Dtos.TrialItem) => {
        Alert.error(
            <NotificationAlert
                alertContent={renderRegistrationFormSaveBlock()}
                icon={FontAwesomeIcons.Solid.BAN}
            />
        );
    };

    const onCancel = () => {
        if (onClose) {
            onClose();
        }
    };

    const loadReduxForm = () => {
        let extraData: Dtos.TrialItem = {
            version: undefined,
        } as Dtos.TrialItem;

        loadForm(reduxFormName, Object.assign({ ...trialItem }, extraData));
    };

    const renderContent = useMemo(() => {
        const formDisabled: boolean =
            savingTrialItem ||
            (formState && formState.formStatus == Dtos.FormStatus.Disabled);

        let formComponents: { [index: string]: RegistrationFormComponent } = {
            version: {
                type: RegistrationComponent.RegistrationControl,
                inputType: TextFormInput,
                inputDisabled: formDisabled,
                validators: {
                    required: (value) =>
                        value != undefined &&
                        value != null &&
                        value?.length > 0,
                },
            },
        };

        return (
            <div className="my-container">
                <Form
                    model={reduxFormName}
                    onSubmit={(val, _event) =>
                        handleOnSubmit(val, currentTrialItem)
                    }
                    onSubmitFailed={(val) => handleOnSubmitFailed(val)}
                >
                    {renderFormControls(
                        form,
                        "trialItem",
                        formComponents,
                        lookups,
                        validationFailures,
                        formProperties,
                        changeForm
                    )}
                    <div className="mt-4 mb-4 text-right">
                        <button
                            disabled={formDisabled}
                            onClick={() => {
                                changeForm(
                                    reduxFormName + ".saveAndReturn",
                                    true
                                );
                            }}
                            type="submit"
                            className="btn btn-primary mr-2"
                        >
                            Submit
                        </button>
                        <button
                            onClick={onCancel}
                            className="btn btn-primary mr-2"
                        >
                            Cancel
                        </button>
                    </div>
                </Form>
            </div>
        );
    }, [
        reduxFormName,
        handleOnSubmit,
        handleOnSubmitFailed,
        renderFormControls,
        form,
        lookups,
        validationFailures,
        formProperties,
        changeForm,
        onCancel,
        currentTrialItem,
    ]);

    return (
        <SubPageLayout loading={loadingTrialItem}>
            {renderContent}
        </SubPageLayout>
    );
};

const mapStateToProps = (
    state: ITmdState,
    ownProps: TrialItemCreateVersionProps
): Partial<TrialItemCreateVersionProps> => {
    let trialItem: Dtos.TrialItem | undefined = !(
        state.trialItem.formData instanceof Array
    )
        ? state.trialItem.formData
        : undefined;

    return {
        currentTrialItem: ownProps.currentTrialItem,
        trialId: ownProps.trialId,
        trialItem: trialItem!,
        loadingTrialItem:
            state.trialItem.loadState &&
            state.trialItem.loadState.status == RequestState.Pending,
        savingTrialItem:
            state.trialItem.saveState &&
            state.trialItem.saveState.status == RequestState.Pending,
        creatingTrialItem:
            state.trialItem.createState &&
            state.trialItem.createState.status == RequestState.Pending,
        createTrialItemSuccess:
            state.trialItem.createState &&
            state.trialItem.createState.status == RequestState.Success,
        lookups: state.trialItem.lookups,
        form: state.reduxForms.trialItem,
        formState: state.trialItem.formState,
        formProperties: state.trialItem.formProperties,
        reduxFormState: state.reduxForms.formState.trialItem,
        validationFailures: state.trialItem.validationFailures,
    };
};

const mapDispatchToProps = (dispatch): Partial<TrialItemCreateVersionProps> => {
    return {
        loadForm: bindActionCreators(actions.load, dispatch),
        changeForm: bindActionCreators(actions.change, dispatch),
        clearTrialItem: bindActionCreators(TrialItemActions.Clear, dispatch),
        saveTrialItem: bindActionCreators(
            TrialItemActions.SaveTrialItem,
            dispatch
        ),
        createTrialItem: bindActionCreators(
            TrialItemActions.CreateTrialItem,
            dispatch
        ),
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialItemCreateVersion);
